/* Meet, or Go live — the one window every call and live is arranged from.
 *
 * TWO THINGS PEOPLE WANT, named for what they are. Audio call, video call
 * and live were three kinds to choose between before a person had said what
 * they were doing. Now:
 *
 *   Meet     a conversation — everybody can talk. Voice, or with cameras.
 *   Go live  one person speaks and the church watches, with chat.
 *
 * Each can start now or be scheduled. From a group's wall it is for that
 * group; from anywhere else the church, a group, or a role within one — and
 * a live can also be for everyone on the app. Either way it can instead be
 * for people chosen by rules — "women aged 20–30", "everybody baptised" —
 * with the count of who that reaches shown as the rules are written. The less-used settings wait
 * under More options; every field the church's own form asked for is still
 * here.
 *
 * Opened from the feed's composer and from Meetings and lives.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Filter, Globe, Mic, Radio, Users, Video } from 'lucide-react'
import {
  useCan,
  useFeatures,
  useGroups,
  useMembers,
  useRoles,
  useScheduleCall,
} from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Field, SelectField } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import { cn, localDateTimeNow } from '@/lib/utils'
import type { AudienceFilter, AudienceKind } from '@/types/api'
import {
  AudienceCount,
  EMPTY_FILTER,
  RuleBuilder,
  completeFilter,
} from './audience/RuleBuilder'

export type ArrangeMode = 'meet' | 'live'

/* Video in a meeting needs a video conferencing service; the one calls run
   on carries voice. The switch is ready and appears when this is true. */
export const VIDEO_MEETINGS = false

type Audience = AudienceKind | 'everyone'

/** Which of Meet and Go live the caller's church offers. A meeting is voice,
 *  or video where that is offered; a church with neither has no Meet. */
export function useArrangeable(): Record<ArrangeMode, boolean> {
  const f = useFeatures()
  return {
    meet: f.audio_conf_calls || (VIDEO_MEETINGS && f.video_conf_calls),
    live: f.live_broadcasts,
  }
}

/** A list of people chosen somewhere else — the absentees from a service
 *  day, say. The meeting is for exactly them. */
export interface ChosenPeople {
  ids: string[]
  /** "12 people absent from Sunday Service" */
  label: string
}

export function ArrangeDialog({
  mode,
  churchId,
  group,
  people,
  initialTitle,
  onClose,
}: {
  mode: ArrangeMode
  churchId: string
  group?: { id: string; name: string }
  people?: ChosenPeople
  initialTitle?: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const schedule = useScheduleCall(churchId)
  /* Everyone on the app is the church's call, not every host's: it puts the
     live in the home feed of people in no church at all. The server refuses
     it without broadcast.public; this only stops offering what would fail. */
  const can = useCan(churchId)
  const mayGoPublic = can('broadcast.public')
  const groups = useGroups(churchId)
  const roles = useRoles(churchId)
  const members = useMembers(churchId)
  const features = useFeatures()
  const arrangeable = useArrangeable()
  const live = mode === 'live'

  const [title, setTitle] = useState(
    initialTitle ?? (group && live ? `${group.name} — live` : ''),
  )
  const [details, setDetails] = useState('')
  const [now, setNow] = useState(true)
  const [when, setWhen] = useState(localDateTimeNow)
  const [audience, setAudience] = useState<Audience>(
    people ? 'list' : group ? 'group' : 'church',
  )
  const [groupId, setGroupId] = useState(group?.id ?? '')
  const [roleId, setRoleId] = useState('')
  const [rules, setRules] = useState<AudienceFilter>(EMPTY_FILTER)
  /* Rules apply across the church unless narrowed to one group. From a
     group's wall they apply within that group. */
  const [withinId, setWithinId] = useState('')
  const [camera, setCamera] = useState(false)
  const [chat, setChat] = useState(true)
  const [comments, setComments] = useState(true)
  const [more, setMore] = useState(false)
  const [moderatorId, setModeratorId] = useState('')
  const [moderatedBy, setModeratedBy] = useState<'all_admins' | 'creator'>('creator')
  const [recording, setRecording] = useState(live)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const needsGroup = audience === 'group' || audience === 'group_role'
  const byRules = audience === 'filter'
  const chosenRules = byRules ? completeFilter(rules) : null
  const ready =
    title.trim().length > 0 &&
    (!needsGroup || groupId) &&
    (audience !== 'group_role' || roleId) &&
    (!byRules || chosenRules !== null) &&
    (audience !== 'list' || (people?.ids.length ?? 0) > 0)

  function submit() {
    if (!ready || schedule.isPending) return
    setError(null)
    const everyone = audience === 'everyone'
    const kind = live ? 'live' : withCamera ? 'video' : 'audio'
    const startsAt = now ? null : new Date(when)
    if (startsAt && startsAt.getTime() < Date.now()) {
      setError('That time has passed. Pick a later one.')
      return
    }
    schedule.mutate(
      {
        kind,
        title: title.trim(),
        description: details.trim() || null,
        starts_at: startsAt ? startsAt.toISOString() : null,
        audience_kind: everyone ? 'church' : (audience as AudienceKind),
        group_id: needsGroup
          ? groupId
          : byRules
            ? group?.id ?? (withinId || null)
            : null,
        role_id: audience === 'group_role' ? roleId : null,
        user_ids: audience === 'list' ? people?.ids : undefined,
        audience_filter: chosenRules,
        is_public: live && everyone && mayGoPublic,
        recording,
        moderator_id: live ? null : moderatorId || null,
        ...(live
          ? { moderated_by: moderatedBy, allow_chat: chat, allow_comments: comments }
          : {}),
        ...(kind === 'video'
          ? {
              host_video: true,
              participant_video: true,
              host_mic: true,
              participant_mic: true,
              max_participants: 12,
            }
          : {}),
      },
      {
        onSuccess: (call) => {
          if (now) {
            navigate(`/calls/${call.id}/${live ? 'live' : 'room'}`)
            return
          }
          setDone(
            startsAt!.toLocaleString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: 'numeric',
              minute: '2-digit',
            }),
          )
        },
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  const heading = live ? 'Go live' : 'Meet'
  /* Voice and video are each the church's to offer: with only one of them
     there is nothing to choose, so the choice is made for the person. */
  const bothKinds = features.audio_conf_calls && features.video_conf_calls
  const withCamera = features.audio_conf_calls ? bothKinds && camera : true

  if (!arrangeable[mode]) {
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title={heading}>
        <div className="space-y-s3 p-s5 text-center">
          <p className="text-base text-ink-2">
            {live
              ? "Going live isn't available for your church."
              : "Meetings aren't available for your church."}
          </p>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    )
  }

  if (done) {
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title={heading}>
        <div className="space-y-s3 p-s5 text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-pill bg-success-soft text-success-ink">
            <Check className="h-6 w-6" aria-hidden />
          </span>
          <p className="text-md font-bold">{title.trim()}</p>
          <p className="text-base text-ink-2">
            Arranged for {done}. Everyone it's for has been told, and it's on
            their Coming up.
          </p>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={heading}
      footer={
        <Button
          variant={live ? 'danger' : 'primary'}
          size="lg"
          full
          onClick={submit}
          disabled={!ready}
          loading={schedule.isPending}
        >
          {live ? <Radio className="h-4 w-4" aria-hidden /> : <Mic className="h-4 w-4" aria-hidden />}
          {now
            ? live
              ? 'Continue to your camera'
              : 'Start meeting'
            : live
              ? 'Schedule live'
              : 'Schedule meeting'}
        </Button>
      }
    >
      <div className="space-y-s4 p-s4">
        <p className="text-base text-ink-2">
          {live
            ? 'You speak, and the church watches — with live chat. It is recorded, and the recording stays on its post.'
            : 'A conversation where everyone can talk. The moderator brings people in to speak.'}
        </p>

        <Field
          label={live ? 'What is it?' : 'Meeting name'}
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 120))}
          placeholder={live ? 'Sunday service' : 'Wednesday Bible study'}
          autoFocus
        />
        <Field
          label="Details"
          value={details}
          onChange={(e) => setDetails(e.target.value.slice(0, 2000))}
          placeholder="Optional — what it's about, or what to bring"
        />

        <Section label="When">
          <Chip active={now} onClick={() => setNow(true)}>
            Now
          </Chip>
          <Chip active={!now} onClick={() => setNow(false)}>
            Schedule for later
          </Chip>
        </Section>
        {!now ? (
          <Field
            label="Date and time"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        ) : null}

        <Section label={live ? 'Who can watch' : "Who's invited"}>
          {people ? (
            <Chip active={audience === 'list'} onClick={() => setAudience('list')}>
              <Users className="h-4 w-4" aria-hidden /> {people.label}
            </Chip>
          ) : null}
          {people ? null : group ? (
            <>
              <Chip active={audience !== 'filter'} onClick={() => setAudience('group')}>
                <Users className="h-4 w-4" aria-hidden /> Everyone in {group.name}
              </Chip>
              <Chip active={byRules} onClick={() => setAudience('filter')}>
                <Filter className="h-4 w-4" aria-hidden /> Only some of them
              </Chip>
            </>
          ) : (
            <>
              <Chip active={audience === 'church'} onClick={() => setAudience('church')}>
                <Users className="h-4 w-4" aria-hidden /> My church
              </Chip>
              <Chip active={audience === 'group'} onClick={() => setAudience('group')}>
                A group
              </Chip>
              <Chip active={audience === 'group_role'} onClick={() => setAudience('group_role')}>
                A role in a group
              </Chip>
              <Chip active={byRules} onClick={() => setAudience('filter')}>
                <Filter className="h-4 w-4" aria-hidden /> People who match…
              </Chip>
              {live && mayGoPublic ? (
                <Chip active={audience === 'everyone'} onClick={() => setAudience('everyone')}>
                  <Globe className="h-4 w-4" aria-hidden /> Everyone
                </Chip>
              ) : null}
            </>
          )}
        </Section>
        {needsGroup && !group ? (
          <div className="grid gap-s3 sm:grid-cols-2">
            <SelectField
              label="Group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              options={[
                { value: '', label: 'Choose a group' },
                ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name })),
              ]}
            />
            {audience === 'group_role' ? (
              <SelectField
                label="Role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                options={[
                  { value: '', label: 'Choose a role' },
                  ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
                ]}
              />
            ) : null}
          </div>
        ) : null}

        {byRules ? (
          <div className="space-y-s3 rounded border border-line-2 p-s3">
            {!group ? (
              <SelectField
                label="Within"
                value={withinId}
                onChange={(e) => setWithinId(e.target.value)}
                options={[
                  { value: '', label: 'The whole church' },
                  ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
            ) : null}
            <RuleBuilder churchId={churchId} value={rules} onChange={setRules} compact />
            <AudienceCount
              churchId={churchId}
              filter={rules}
              groupId={group?.id ?? (withinId || null)}
            />
          </div>
        ) : null}

        {!live && VIDEO_MEETINGS && bothKinds ? (
          <Section label="Camera">
            <Chip active={!camera} onClick={() => setCamera(false)}>
              <Mic className="h-4 w-4" aria-hidden /> Voice only
            </Chip>
            <Chip active={camera} onClick={() => setCamera(true)}>
              <Video className="h-4 w-4" aria-hidden /> With video
            </Chip>
          </Section>
        ) : null}

        {live ? (
          <Section label="While it's on">
            <Chip active={chat} onClick={() => setChat((v) => !v)} check>
              Live chat
            </Chip>
            <Chip active={comments} onClick={() => setComments((v) => !v)} check>
              Comments
            </Chip>
          </Section>
        ) : null}

        <div>
          <button
            type="button"
            onClick={() => setMore((v) => !v)}
            aria-expanded={more}
            className="flex items-center gap-1 text-sm font-semibold text-ink-2 hover:text-ink"
          >
            More options
            <ChevronDown className={cn('h-4 w-4 transition-transform', more && 'rotate-180')} aria-hidden />
          </button>
          {more ? (
            <div className="mt-s3 space-y-s3">
              {live ? (
                <SelectField
                  label="Who can run it"
                  value={moderatedBy}
                  onChange={(e) => setModeratedBy(e.target.value as 'all_admins' | 'creator')}
                  options={[
                    { value: 'creator', label: 'Only me' },
                    { value: 'all_admins', label: 'Every administrator' },
                  ]}
                />
              ) : (
                <SelectField
                  label="Moderator"
                  value={moderatorId}
                  onChange={(e) => setModeratorId(e.target.value)}
                  help="They bring people in to speak, mute, and end the meeting."
                  options={[
                    { value: '', label: 'Me' },
                    ...(members.data ?? []).map((m) => ({ value: m.id, label: m.name })),
                  ]}
                />
              )}
              <div className="flex flex-wrap gap-s2">
                <Chip active={recording} onClick={() => setRecording((v) => !v)} check>
                  Record it
                </Chip>
              </div>
            </div>
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger-ink">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-sm font-semibold text-ink-2">{label}</span>
      <div className="mt-1 flex flex-wrap gap-s2">{children}</div>
    </div>
  )
}

function Chip({
  active,
  onClick,
  check,
  children,
}: {
  active: boolean
  onClick: () => void
  check?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-1.5 rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
        active ? 'border-brand bg-brand-softer text-brand' : 'border-line-2 text-ink-2 hover:bg-hover',
      )}
    >
      {check && active ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
      {children}
    </button>
  )
}
